import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface ImpersonationState {
  isImpersonating: boolean;
  impersonatedOrgId: string | null;
  impersonatedOrgName: string | null;
  impersonatedRole: 'client_admin' | 'exec' | null;
  impersonatedByUserId: string | null;
}

export interface AdminOrg {
  id: string;
  name: string;
}

interface AdminContextType extends ImpersonationState {
  startImpersonation: (orgId: string, orgName: string, role?: 'client_admin' | 'exec') => void;
  exitImpersonation: () => void;
  /** All orgs available in the admin view */
  orgs: AdminOrg[];
  orgsLoading: boolean;
  /** Currently selected org filter — null means "All orgs" */
  selectedOrgId: string | null;
  selectedOrgName: string | null;
  setSelectedOrg: (orgId: string | null) => void;
}

const IMPERSONATION_KEY = 'admin_impersonation';
const ORG_FILTER_KEY = 'admin_org_filter';
const ADMIN_CONTEXT_USER_KEY = 'admin_context_user_id';

const emptyImpersonationState: ImpersonationState = {
  isImpersonating: false,
  impersonatedOrgId: null,
  impersonatedOrgName: null,
  impersonatedRole: null,
  impersonatedByUserId: null,
};

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export function AdminProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState<ImpersonationState>(() => {
    // Initialize from localStorage
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(IMPERSONATION_KEY);
      if (stored) {
        try {
          return { ...emptyImpersonationState, ...JSON.parse(stored) };
        } catch {
          localStorage.removeItem(IMPERSONATION_KEY);
        }
      }
    }
    return emptyImpersonationState;
  });

  // Org list + filter
  const [orgs, setOrgs] = useState<AdminOrg[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [selectedOrgId, setSelectedOrgIdState] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(ORG_FILTER_KEY) || null;
    }
    return null;
  });

  // Fetch orgs once on mount
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('organizations')
        .select('id, name')
        .order('name');
      setOrgs((data || []) as AdminOrg[]);
      setOrgsLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (authLoading || !user?.id || typeof window === 'undefined') return;

    const currentUserId = user.id;
    const previousUserId = localStorage.getItem(ADMIN_CONTEXT_USER_KEY);
    const impersonationBelongsToCurrentUser = state.impersonatedByUserId === currentUserId;
    const hasLegacyImpersonation = state.isImpersonating && !state.impersonatedByUserId;
    const userChanged = previousUserId && previousUserId !== currentUserId;

    if (userChanged || hasLegacyImpersonation || (state.isImpersonating && !impersonationBelongsToCurrentUser)) {
      localStorage.removeItem(IMPERSONATION_KEY);
      localStorage.removeItem(ORG_FILTER_KEY);
      localStorage.setItem(ADMIN_CONTEXT_USER_KEY, currentUserId);
      setSelectedOrgIdState(null);
      setState(emptyImpersonationState);
      return;
    }

    localStorage.setItem(ADMIN_CONTEXT_USER_KEY, currentUserId);
  }, [authLoading, state.impersonatedByUserId, state.isImpersonating, user?.id]);

  const selectedOrgName = selectedOrgId
    ? orgs.find(o => o.id === selectedOrgId)?.name || null
    : null;

  const setSelectedOrg = useCallback((orgId: string | null) => {
    setSelectedOrgIdState(orgId);
    if (orgId) {
      localStorage.setItem(ORG_FILTER_KEY, orgId);
    } else {
      localStorage.removeItem(ORG_FILTER_KEY);
    }
  }, []);

  // Persist state to localStorage
  useEffect(() => {
    if (state.isImpersonating) {
      localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(state));
    } else {
      localStorage.removeItem(IMPERSONATION_KEY);
    }
  }, [state]);

  const startImpersonation = useCallback((orgId: string, orgName: string, role: 'client_admin' | 'exec' = 'client_admin') => {
    setState({
      isImpersonating: true,
      impersonatedOrgId: orgId,
      impersonatedOrgName: orgName,
      impersonatedRole: role,
      impersonatedByUserId: user?.id || null,
    });
  }, [user?.id]);

  const exitImpersonation = useCallback(() => {
    setState(emptyImpersonationState);
  }, []);

  return (
    <AdminContext.Provider value={{
      ...state,
      startImpersonation,
      exitImpersonation,
      orgs,
      orgsLoading,
      selectedOrgId,
      selectedOrgName,
      setSelectedOrg,
    }}>
      {children}
    </AdminContext.Provider>
  );
}

export function useAdminContext() {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error('useAdminContext must be used within an AdminProvider');
  }
  return context;
}
